import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Box, Chip, IconButton, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Typography } from "@mui/material";
import { useState } from "react";
import { ToastContent } from "../../../shared/components";
import { useToast } from "../../../shared/hooks";
import { colorBackGroundStatus, colorTextStatus, globalBackgroundSetup, globalColorChipSetup, globalMaskDocument, globalTranslateStatusInvoices, translateStatusClient } from "../../../shared/utils";
import { formatDateFnsRemoveTimezone } from "../../../shared/utils/format-date";
import { FinancialService, IListInvoice } from "../../../shared/services/api/financial/FinancialService";
import { ModalDetailInvoice } from "../../financial/components/ModalDetailInvoice";
import { BlockInvoiceMenu, BlockInvoiceMenuAction } from "./Menus/BlockInvoiceMenu";
import { ModalBlockInvoice } from "./Modals/ModalBlockInvoice";
import { ModalNotifyBlockInvoice } from "./Modals/ModalNotifyBlockInvoice";

export interface IBlockInvoiceListRow {
	clientId: string;
	client: IListInvoice["client"];
	createdAt: string;
	dueDate: string;
	reference: string;
	groupedAt?: string | null;
	groupedNumber?: string | number;
	representativeInvoice: IListInvoice;
	invoices: IListInvoice[];
}

interface TableBlockInvoiceProps {
	rows: IBlockInvoiceListRow[];
	page: number;
	rowsPerPage: number;
	totalCount: number;
	onPageChange: (newPage: number) => void;
	onRowsPerPageChange: (newRowsPerPage: number) => void;
	onRefresh: () => void;
	onBlockedClient?: (clientId: string) => void;
}

export const TableBlockInvoice = ({ rows, page, rowsPerPage, totalCount, onPageChange, onRowsPerPageChange, onRefresh, onBlockedClient }: TableBlockInvoiceProps) => {
	const toastHandler = useToast();
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [selectedBlockRow, setSelectedBlockRow] = useState<IBlockInvoiceListRow | null>(null);
	const [selectedRow, setSelectedRow] = useState<IListInvoice | null>(null);
	const [isDetailOpen, setIsDetailOpen] = useState(false);
	const [isNotifyOpen, setIsNotifyOpen] = useState(false);
	const [isBlockConfirmOpen, setIsBlockConfirmOpen] = useState(false);
	const [isBlockingClient, setIsBlockingClient] = useState(false);

	const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, row: IBlockInvoiceListRow) => {
		setAnchorEl(event.currentTarget);
		setSelectedBlockRow(row);
		setSelectedRow(row.representativeInvoice);
	};

	const handleMenuClose = () => {
		setAnchorEl(null);
	};

	const handleOpenAction = (action: BlockInvoiceMenuAction, row: IListInvoice) => {
		setSelectedRow(row);

		if (action === "details") {
			setIsDetailOpen(true);
			return;
		}

		if (action === "notifyClient") {
			setIsNotifyOpen(true);
			return;
		}

		if (action === "blockClient") {
			setIsBlockConfirmOpen(true);
		}
	};

	const handleCloseDetails = () => {
		setIsDetailOpen(false);
	};

	const handleCloseNotify = () => {
		setIsNotifyOpen(false);
	};

	const handleCloseBlockConfirm = () => {
		if (isBlockingClient) return;
		setIsBlockConfirmOpen(false);
	};

	const handleConfirmBlockClient = async () => {
		if (!selectedRow?.clientId || !selectedBlockRow) return;
	};

	const handleSubmitBlockClient = async ({ subject, message }: { subject: string; message: string }) => {
		if (!selectedRow?.clientId) return;

		setIsBlockingClient(true);
		try {
			const result = await FinancialService.cancelBlockClient(selectedRow.clientId, {
				subject,
				message,
			});

			if (result instanceof Error) {
				toastHandler.present({
					type: "error",
					position: "top-right",
					messageOrContent: <ToastContent title="Erro" content={result.message || "Nao foi possivel bloquear a imobiliaria."} />,
				});
				return;
			}

			setIsBlockConfirmOpen(false);
			toastHandler.present({
				type: "success",
				position: "top-right",
				messageOrContent: <ToastContent title="Bloqueio realizado" content="A imobiliaria foi bloqueada com sucesso." />,
			});
			onBlockedClient?.(selectedRow.clientId);
			onRefresh();
		} catch (error) {
			const errorMessage = error instanceof Error ? error.message : "Nao foi possivel bloquear a imobiliaria.";
			toastHandler.present({
				type: "error",
				position: "top-right",
				messageOrContent: <ToastContent title="Erro" content={errorMessage} />,
			});
		} finally {
			setIsBlockingClient(false);
		}
	};

	return (
		<>
			<TableContainer component={Paper} sx={{ mt: 1, width: "96%", px: { xs: 1, sm: 3 }, py: { xs: 2, sm: 3 }, ml: 1, borderRadius: 2, overflow: "hidden" }}>
				<Table size="small" stickyHeader aria-label="Tabela de bloqueios">
					<TableHead>
						<TableRow sx={{ backgroundColor: "grey.100" }}>
							{["Empresa", "Criação", "Vencimento", "Referência", "Agrupamento", "Status", "Ações"].map((label, index) => (
								<TableCell key={label} align={index === 6 ? "right" : "left"}>
									<Typography variant="button">{label}</Typography>
								</TableCell>
							))}
						</TableRow>
					</TableHead>
					<TableBody>
						{rows.map((row) => (
							<TableRow key={row.clientId} hover>
								<TableCell>
									<Stack spacing={0.5}>
										<Typography fontWeight="bold">{row.client?.name || row.client?.trade_name || "-"}</Typography>
										<Typography variant="body2" color="textSecondary">
											{globalMaskDocument(row.client?.document || "")}
										</Typography>
										<Typography variant="body2" color="textSecondary">
											{row.client?.email || "-"}
										</Typography>
									</Stack>
								</TableCell>
								<TableCell>{formatDateFnsRemoveTimezone(row.createdAt)}</TableCell>
								<TableCell>{formatDateFnsRemoveTimezone(row.dueDate)}</TableCell>
								<TableCell>
									<Typography fontWeight={500}>{row.reference || "-"}</Typography>
									{row.invoices.length > 1 ? (
										<Typography variant="body2" color="textSecondary">
											{row.invoices.length} registros para esta empresa
										</Typography>
									) : null}
								</TableCell>
								<TableCell>
									<Stack spacing={0.5} alignItems="flex-start">
										<Chip
											size="medium"
											label={row.groupedAt ? `Agrupado ${row.groupedAt}` : "Sem agrupamento"}
											sx={{
												color: globalColorChipSetup(row.representativeInvoice.status),
												fontWeight: 500,
												background: globalBackgroundSetup(row.representativeInvoice.status),
												border: "0.5px solid #f1f1f1",
											}}
										/>
										<Typography variant="body2" color="textSecondary">
											{row.groupedNumber ? `Quantidade agrupada: ${row.groupedNumber}` : globalTranslateStatusInvoices(row.representativeInvoice.status)}
										</Typography>
									</Stack>
								</TableCell>
								<TableCell>
									<Chip
										size="medium"
										label={translateStatusClient(row.client?.status || "")}
										sx={{
											color: colorTextStatus(row.client?.status || ""),
											fontWeight: 500,
											background: colorBackGroundStatus(row.client?.status || ""),
											border: "0.5px solid #f1f1f1",
										}}
									/>
								</TableCell>
								<TableCell align="right">
									<IconButton onClick={(event) => handleMenuOpen(event, row)} size="small" aria-label={`Ações para ${row.client?.name || row.clientId}`}>
										<MoreVertIcon fontSize="small" />
									</IconButton>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>

				<Box display="flex" justifyContent="flex-end" alignItems="center" mt={2} pr={2}>
					<TablePagination
						component="div"
						count={totalCount}
						page={page}
						rowsPerPage={rowsPerPage}
						onPageChange={(_, newPage) => onPageChange(newPage)}
						onRowsPerPageChange={(event) => onRowsPerPageChange(parseInt(event.target.value, 10))}
						rowsPerPageOptions={[10, 25, 50]}
						labelRowsPerPage="Linhas por página:"
						labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count} registros`}
					/>
				</Box>
			</TableContainer>

			<BlockInvoiceMenu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose} selectedRow={selectedRow} onOpenAction={handleOpenAction} />

			{selectedRow && isDetailOpen && <ModalDetailInvoice rowInvoice={selectedRow} isOpenModal={true} onCloseModal={handleCloseDetails} />}
			{selectedBlockRow && isNotifyOpen && <ModalNotifyBlockInvoice open={true} onClose={handleCloseNotify} row={selectedBlockRow} />}
				{selectedBlockRow && <ModalBlockInvoice open={isBlockConfirmOpen} onClose={handleCloseBlockConfirm} onConfirm={handleSubmitBlockClient} row={selectedBlockRow} isSubmitting={isBlockingClient} />}
		</>
	);
};